import React, { useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert } from 'antd';
import { useFormik } from 'formik';

import { ExportsForm } from './ExportsForm';
import {
    ExportFormValues,
    ExportsModalProps,
} from '../interface/exports.interface';
import { validationSchema } from '../utils/fieldRender';
import { selectShopCurrent } from '../../../../shops/features/shops/store/shopSlice';
import { GenericModal } from '../../../../../shared/ui/GenericModal';
import getEnvConfig from '../../../../../utils/config/env';
import { useAppSelector } from '../../../../../store';

const exportTypes = ['visitors', 'orders'] as const;
type ExportType = (typeof exportTypes)[number];

const getInitialTypeFromPath = (pathname: string): ExportType | undefined =>
    exportTypes.find(type => pathname.includes(type));

export const ExportsModal: React.FC<ExportsModalProps> = ({
    createExport,
    isSuccess,
    isLoading,
}) => {
    const navigate = useNavigate();
    const current = useAppSelector(selectShopCurrent);
    const { pathname } = useLocation();

    const initialType = useMemo(
        () => getInitialTypeFromPath(pathname) ?? '',
        [pathname]
    );

    const formik = useFormik<ExportFormValues>({
        initialValues: {
            shop_id: current.id ? +current.id : undefined,
            type: initialType,
            start_at: '',
            end_at: '',
        },
        validationSchema,
        onSubmit: () => {},
        validateOnChange: true,
        enableReinitialize: true,
    });

    useEffect(() => {
        if (formik.values.type !== initialType) {
            formik.setFieldValue('type', initialType);
        }
    }, [initialType]);

    useEffect(() => {
        if (current.id && formik.values.shop_id !== +current.id) {
            formik.setFieldValue('shop_id', +current.id);
        }
    }, [current.id]);

    useEffect(() => {
        if (isSuccess) {
            formik.resetForm();
        }
    }, [isSuccess]);

    const onConfirm = useCallback(async () => {
        await createExport(formik.values);
    }, [createExport, formik.values]);

    const close = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onView = useCallback(() => {
        if (pathname.includes('exports')) {
            close();
        } else {
            navigate(`${getEnvConfig().PUBLIC_PATH}/crm/exports`);
        }
    }, [pathname, close, navigate]);

    return (
        <GenericModal
            okButtonProps={{
                disabled: isLoading,
                loading: isLoading,
            }}
            onConfirm={isSuccess ? onView : onConfirm}
            okText={isSuccess ? 'Перейти' : 'Сформировать выгрузку'}
            onCancel={close}
            title={'Выгрузить в XLSX'}
            open
        >
            <div style={{ marginTop: 16 }}>
                <ExportsForm formik={formik} />
            </div>
            {isSuccess && (
                <div style={{ marginTop: 24 }}>
                    <Alert
                        banner
                        message='Файл формируется, сохранить файл можно во вкладке “Выгрузка” '
                        type='info'
                    />
                </div>
            )}
        </GenericModal>
    );
};
